import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { PROFILE_DATA } from '../../data/config';
import { Mail, Phone, MapPin, Copy, Check, Send } from 'lucide-react';

const Contact = () => {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(PROFILE_DATA.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real application, you would send this data to a backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="section-container">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="mb-16 max-w-3xl">
          <p className="text-[0.7rem] uppercase tracking-[0.4em] text-coffee-300 mb-5">Contact</p>
          <h2 className="text-5xl md:text-7xl font-bold mb-6 gradient-text leading-[1.05]">
            Let's talk.
          </h2>
          <p className="text-lg md:text-xl text-cream-200/80 leading-relaxed">
            Let's discuss how we can work together.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <div className="space-y-6">
            {/* Contact Cards */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-cream-100 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                {/* Email */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-coffee-700/20 hover:bg-coffee-700/30 hover:border-caramel-400/30 border border-transparent transition-all group">
                  <div className="w-10 h-10 rounded-lg bg-coffee-600/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-caramel-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-coffee-300 mb-1">Email</div>
                    <div className="text-cream-100 font-mono text-sm break-all">
                      {PROFILE_DATA.email}
                    </div>
                  </div>
                  <button
                    onClick={copyToClipboard}
                    className="p-2 rounded-lg bg-coffee-600/30 hover:bg-coffee-600/50 transition-colors"
                    title="Copy email"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-caramel-300" />
                    ) : (
                      <Copy className="w-4 h-4 text-coffee-300" />
                    )}
                  </button>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-coffee-700/20 hover:bg-coffee-700/30 hover:border-caramel-400/30 border border-transparent transition-all">
                  <div className="w-10 h-10 rounded-lg bg-coffee-600/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-caramel-300" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-coffee-300 mb-1">Phone</div>
                    <div className="text-cream-100 font-mono text-sm">
                      {PROFILE_DATA.phone}
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-coffee-700/20 hover:bg-coffee-700/30 hover:border-caramel-400/30 border border-transparent transition-all">
                  <div className="w-10 h-10 rounded-lg bg-coffee-600/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-caramel-300" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-coffee-300 mb-1">Location</div>
                    <div className="text-cream-100">
                      {PROFILE_DATA.location.city}, {PROFILE_DATA.location.country}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-cream-100 mb-4">Location</h3>
              <div className="relative rounded-xl overflow-hidden h-64 bg-espresso-900 ring-1 ring-coffee-700/40">
                <MapContainer
                  center={PROFILE_DATA.location.coords}
                  zoom={13}
                  scrollWheelZoom={false}
                  className="h-full w-full"
                  style={{ filter: 'sepia(25%) hue-rotate(-8deg) saturate(0.9) brightness(0.95)' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  />
                  <Marker position={PROFILE_DATA.location.coords}>
                    <Popup>
                      {PROFILE_DATA.location.city}, {PROFILE_DATA.location.country}
                    </Popup>
                  </Marker>
                </MapContainer>
                {/* Coffee-tone gradient fade — softens the top edge */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso-900/40 via-transparent to-espresso-900/30"></div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card p-8">
            <h3 className="text-xl font-semibold text-cream-100 mb-6">Send a Message</h3>
            
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="w-16 h-16 bg-caramel-400/20 rounded-full flex items-center justify-center mb-4 ring-2 ring-caramel-400/40"
                >
                  <Check className="w-8 h-8 text-caramel-300" />
                </motion.div>
                <h4 className="text-xl font-semibold text-cream-100 mb-2">Message Sent!</h4>
                <p className="text-coffee-200 text-center">
                  Thank you for reaching out. I'll get back to you soon.
                </p>
              </div>
            ) : (
              <motion.form
                onSubmit={handleSubmit}
                className="space-y-6"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-50px' }}
                variants={{ show: { transition: { staggerChildren: 0.08 } } }}
              >
                {/* Name */}
                <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}>
                  <label htmlFor="name" className="block text-xs uppercase tracking-wider font-semibold text-coffee-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-espresso-850/60 border border-coffee-700/40 rounded-xl text-cream-100 placeholder-coffee-300/60 focus:outline-none focus:border-caramel-400 focus:ring-2 focus:ring-caramel-400/30 focus:bg-espresso-850/80 hover:border-coffee-600/60 transition-all"
                    placeholder="Your name"
                  />
                </motion.div>

                {/* Email */}
                <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}>
                  <label htmlFor="email" className="block text-xs uppercase tracking-wider font-semibold text-coffee-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-espresso-850/60 border border-coffee-700/40 rounded-xl text-cream-100 placeholder-coffee-300/60 focus:outline-none focus:border-caramel-400 focus:ring-2 focus:ring-caramel-400/30 focus:bg-espresso-850/80 hover:border-coffee-600/60 transition-all"
                    placeholder="your.email@example.com"
                  />
                </motion.div>

                {/* Message */}
                <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}>
                  <label htmlFor="message" className="block text-xs uppercase tracking-wider font-semibold text-coffee-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 bg-espresso-850/60 border border-coffee-700/40 rounded-xl text-cream-100 placeholder-coffee-300/60 focus:outline-none focus:border-caramel-400 focus:ring-2 focus:ring-caramel-400/30 focus:bg-espresso-850/80 hover:border-coffee-600/60 transition-all resize-none"
                    placeholder="Tell me about your project..."
                  ></textarea>
                </motion.div>

                {/* Submit Button */}
                <motion.button
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center gap-2 group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Send className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  Send Message
                </motion.button>
              </motion.form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
